import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isSuperAdmin } from '../utils/adminAccess';
import { API_BASE_URL } from '../config';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';

interface TenantRow {
  tenantId: string;
  orgName: string;
  plan: string;
  accountStatus: string;
  isDeleted: boolean;
  scheduledDeletionDate: string;
  deletionOverdue: boolean;
  stripeCustomerId: string;
  createdAt: string;
  userCount: number;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('idToken') || '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function fetchTenants(): Promise<TenantRow[]> {
  const res = await fetch(`${API_BASE_URL}/super-admin/tenants`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const data = await res.json();
  return data.tenants as TenantRow[];
}

async function triggerCleanup(tenantId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/super-admin/tenants/${encodeURIComponent(tenantId)}/trigger-cleanup`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `${res.status} ${res.statusText}`);
  }
}

const PLAN_TONE: Record<string, string> = {
  basic: 'neutral',
  pro: 'primary',
  enterprise: 'success',
};

const PLAN_LABEL: Record<string, string> = {
  basic: 'Basic',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

const STATUS_TONE: Record<string, string> = {
  active: 'success',
  DELETION_PENDING: 'warning',
  deleted: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  DELETION_PENDING: 'Deletion Pending',
  deleted: 'Deleted',
};

export const SuperAdminPage: React.FC = () => {
  const toast = useToast();
  const swal = useSwal();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { data: tenants = [], isLoading, error } = useQuery<TenantRow[]>({
    queryKey: ['super-admin-tenants'],
    queryFn: fetchTenants,
    staleTime: 30_000,
  });

  const cleanupMutation = useMutation({
    mutationFn: triggerCleanup,
    onSuccess: (_, tenantId) => {
      toast.success(`Cleanup triggered for ${tenantId}`);
      queryClient.invalidateQueries({ queryKey: ['super-admin-tenants'] });
    },
    onError: (err: Error) => {
      toast.error(`Failed: ${err.message}`);
    },
  });

  const handleTriggerCleanup = async (t: TenantRow) => {
    const confirmed = await swal.confirm(
      `Trigger immediate data deletion for "${t.orgName}"?`,
      'This cannot be undone.',
    );
    if (!confirmed) return;
    cleanupMutation.mutate(t.tenantId);
  };

  const searched = useMemo(() => {
    const q = search.toLowerCase();
    return tenants.filter((t) =>
      (!q ||
        t.orgName.toLowerCase().includes(q) ||
        t.tenantId.toLowerCase().includes(q) ||
        t.plan.toLowerCase().includes(q))
      && (!filterPlan || t.plan === filterPlan)
      && (!filterStatus || (t.accountStatus || 'active') === filterStatus),
    );
  }, [tenants, search, filterPlan, filterStatus]);

  const { sorted: filtered, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<TenantRow, keyof TenantRow>(
    searched,
    (t, f) => (f === 'userCount' ? t.userCount : String(t[f] ?? '')),
    'orgName',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  if (!isSuperAdmin()) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          <span>Unauthorized: This page is only accessible to Mercell super-admins.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-shield-keyhole-line"
        title="Super Admin Portal"
        subtitle="Tenant overview — Mercell operator access only"
        colour="danger"
        actions={
          <span className="np-chip np-tone-danger">
            <i className="ri-shield-keyhole-line"></i>Internal
          </span>
        }
      />

      {/* Summary cards */}
      <div className="np-grid np-grid-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-primary"><i className="ri-building-line"></i></span>
            <p className="np-stat-label">Total Tenants</p>
            <p className="np-stat-value np-tabular">{tenants.length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
            <p className="np-stat-label">Active</p>
            <p className="np-stat-value np-tabular">{tenants.filter((t) => !t.isDeleted).length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-warning"><i className="ri-time-line"></i></span>
            <p className="np-stat-label">Deletion Pending</p>
            <p className="np-stat-value np-tabular">{tenants.filter((t) => t.isDeleted && !t.deletionOverdue).length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-danger"><i className="ri-alert-line"></i></span>
            <p className="np-stat-label">Overdue Cleanup</p>
            <p className="np-stat-value np-tabular">{tenants.filter((t) => t.deletionOverdue).length}</p>
          </div>
        </div>
      </div>

      <div className="np-card">
        <div className="np-card-head">
          <h2>Tenants</h2>
        </div>

        {/* Filters toolbar */}
        <div className="np-card-body border-bottom">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <div className="np-search" style={{ maxWidth: 260 }}>
              <i className="ri-search-line"></i>
              <input
                placeholder="Search by name, ID or plan…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="np-icon-btn" onClick={() => setSearch('')}><i className="ri-close-line"></i></button>
              )}
            </div>
            <select
              className="np-select"
              style={{ maxWidth: 160 }}
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
            >
              <option value="">All Plans</option>
              {Object.keys(PLAN_TONE).map((p) => (
                <option key={p} value={p}>{PLAN_LABEL[p] || p}</option>
              ))}
            </select>
            <select
              className="np-select"
              style={{ maxWidth: 180 }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              {Object.keys(STATUS_TONE).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
              ))}
            </select>
            {(search || filterPlan || filterStatus) && (
              <button
                className="np-btn np-btn-ghost np-btn-sm"
                onClick={() => { setSearch(''); setFilterPlan(''); setFilterStatus(''); }}
              >
                <i className="ri-filter-off-line"></i>Clear
              </button>
            )}
            <span className="np-sub ms-auto">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="np-card-body p-0">
          {isLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
          {error && (
            <div className="np-banner np-tone-danger m-3">
              <i className="ri-error-warning-line"></i>
              <span>Failed to load tenants: {(error as Error).message}</span>
            </div>
          )}
          {!isLoading && !error && (
            <>
              <NimbusTable
                thead={
                  <tr>
                    <SortableTh field="orgName" active={sortField === 'orgName'} dir={sortDir} onSort={toggleSort}>Organisation</SortableTh>
                    <SortableTh field="plan" active={sortField === 'plan'} dir={sortDir} onSort={toggleSort}>Plan</SortableTh>
                    <SortableTh field="accountStatus" active={sortField === 'accountStatus'} dir={sortDir} onSort={toggleSort}>Status</SortableTh>
                    <SortableTh field="userCount" active={sortField === 'userCount'} dir={sortDir} onSort={toggleSort}>Users</SortableTh>
                    <SortableTh field="stripeCustomerId" active={sortField === 'stripeCustomerId'} dir={sortDir} onSort={toggleSort}>Stripe Customer</SortableTh>
                    <SortableTh field="createdAt" active={sortField === 'createdAt'} dir={sortDir} onSort={toggleSort}>Created</SortableTh>
                    <SortableTh field="scheduledDeletionDate" active={sortField === 'scheduledDeletionDate'} dir={sortDir} onSort={toggleSort}>Deletion Date</SortableTh>
                    <th></th>
                  </tr>
                }
                isEmpty={pageItems.length === 0}
                emptyState={
                  <div className="text-center np-sub py-4">No tenants found.</div>
                }
              >
                {pageItems.map((t) => (
                  <tr key={t.tenantId} className={t.deletionOverdue ? 'np-tone-danger' : ''}>
                    <td>
                      <div className="fw-semibold">{t.orgName || '—'}</div>
                      <small className="np-hint">{t.tenantId}</small>
                    </td>
                    <td>
                      <span className={`np-chip np-tone-${PLAN_TONE[t.plan] || 'neutral'}`}>
                        {t.plan}
                      </span>
                    </td>
                    <td>
                      <span className={`np-chip np-tone-${STATUS_TONE[t.accountStatus] || 'neutral'}`}>
                        {t.accountStatus || 'active'}
                      </span>
                    </td>
                    <td className="np-tabular">{t.userCount}</td>
                    <td>
                      {t.stripeCustomerId ? (
                        <code className="small">{t.stripeCustomerId}</code>
                      ) : (
                        <span className="np-hint">—</span>
                      )}
                    </td>
                    <td className="np-tabular np-hint">
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="np-tabular">
                      {t.scheduledDeletionDate ? (
                        <span className={t.deletionOverdue ? 'text-danger fw-semibold' : 'text-warning'}>
                          {new Date(t.scheduledDeletionDate).toLocaleDateString()}
                          {t.deletionOverdue && ' ⚠ overdue'}
                        </span>
                      ) : (
                        <span className="np-hint">—</span>
                      )}
                    </td>
                    <td>
                      {t.deletionOverdue && (
                        <button
                          className="np-btn np-btn-danger np-btn-sm"
                          disabled={cleanupMutation.isPending}
                          onClick={() => handleTriggerCleanup(t)}
                        >
                          <i className="ri-delete-bin-line"></i>Delete Now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </NimbusTable>
              {pageItems.length > 0 && (
                <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
